import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Video, ChevronLeft, RefreshCw, Loader2, Eye, EyeOff } from "lucide-react";
import LessonEditor from "./LessonEditor";

const ROLES = ["super_admin", "trainer", "internal_staff", "partner"];
const ROLE_LABEL = { super_admin: "Super admin", trainer: "Trainer", internal_staff: "Internal staff", partner: "Partner" };
const EMPTY = { title: "", description: "", category: "General", cover_image: "", visibility_roles: [...ROLES], sequential_unlock: false, pass_mark: 80, is_published: true };

export default function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [current, setCurrent] = useState(null);
  const [courseForm, setCourseForm] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadList = useCallback(async () => { const { data } = await api.get("/admin/courses"); setCourses(data); }, []);
  const loadCourse = useCallback(async (id) => { const { data } = await api.get(`/admin/courses/${id}`); setCurrent(data); }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const saveCourse = async () => {
    try {
      if (courseForm.id) await api.put(`/admin/courses/${courseForm.id}`, courseForm);
      else await api.post("/admin/courses", courseForm);
      toast.success("Course saved");
      setCourseForm(null); await loadList(); if (current) await loadCourse(current.id);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deleteCourse = async (c) => {
    if (!window.confirm(`Delete "${c.title}" and all its lessons?`)) return;
    try { await api.delete(`/admin/courses/${c.id}`); toast.success("Course deleted"); setCurrent(null); await loadList(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const addModule = async () => {
    const title = window.prompt("Module title");
    if (!title) return;
    await api.post(`/admin/courses/${current.id}/modules`, { title }); await loadCourse(current.id);
  };
  const renameModule = async (m) => {
    const title = window.prompt("Module title", m.title);
    if (!title || title === m.title) return;
    await api.put(`/admin/modules/${m.id}`, { title }); await loadCourse(current.id);
  };
  const deleteModule = async (m) => {
    try { await api.delete(`/admin/modules/${m.id}`); await loadCourse(current.id); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deleteLesson = async (l) => {
    if (!window.confirm(`Delete lesson "${l.title}"?`)) return;
    await api.delete(`/admin/lessons/${l.id}`); await loadCourse(current.id); await loadList();
  };
  const refreshContent = async () => {
    if (!window.confirm("Reload the built-in lesson text and assessment questions for the three foundation courses? Your videos and member progress are kept, but any text edits to those lessons will be overwritten.")) return;
    setRefreshing(true);
    try { const { data } = await api.post("/admin/content/refresh"); toast.success(`Updated ${data.updated} lessons`); await loadList(); if (current) await loadCourse(current.id); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setRefreshing(false); }
  };

  if (current) {
    return (
      <div data-testid="admin-course-editor">
        <button onClick={() => setCurrent(null)} className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="course-editor-back">
          <ChevronLeft className="h-4 w-4" /> All courses
        </button>
        <div className="card-surface p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-heading text-[22px] font-semibold">{current.title}</h2>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${current.is_published ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600"}`}>{current.is_published ? "Published" : "Draft"}</span>
              </div>
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{current.description}</p>
              <p className="mt-1 text-xs text-muted-foreground">Visible to: {current.visibility_roles.map((r) => ROLE_LABEL[r]).join(", ")}{current.sequential_unlock ? ". Lessons unlock in order." : ""}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setCourseForm({ ...current })} data-testid="edit-course-button"><Pencil className="mr-1.5 h-3.5 w-3.5" /> Course settings</Button>
              <Button variant="outline" size="sm" onClick={addModule} data-testid="add-module-button"><Plus className="mr-1.5 h-3.5 w-3.5" /> Module</Button>
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          {current.modules.map((m, mi) => (
            <div key={m.id} className="card-surface p-4 sm:p-5" data-testid={`module-card-${mi}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="font-semibold">Module {mi + 1}: {m.title}</div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => renameModule(m)} data-testid={`rename-module-${mi}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteModule(m)} data-testid={`delete-module-${mi}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                  <Button size="sm" className="btn-brand ml-2" onClick={() => setLesson({ module_id: m.id, title: "", content: "", takeaways: [], cpd_minutes: 15, video_provider: null, video_url: "" })} data-testid={`add-lesson-${mi}`}><Plus className="mr-1 h-3.5 w-3.5" /> Lesson</Button>
                </div>
              </div>
              <div className="mt-3 divide-y divide-border">
                {m.lessons.map((l) => (
                  <div key={l.id} className="flex items-center gap-3 py-2.5" data-testid={`lesson-row-${l.id}`}>
                    <div className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${l.video_url ? "bg-[#33B6E4]/15 text-[#1779AC]" : "bg-secondary text-muted-foreground"}`} title={l.video_url ? `Video: ${l.video_provider}` : "No video yet"}>
                      <Video className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{l.title}</div>
                      <div className="text-xs text-muted-foreground">{l.cpd_minutes} min. {l.video_url ? `Video (${l.video_provider})` : "Audio narration only"}{l.heygen?.status && l.heygen.status !== "completed" ? `. HeyGen: ${l.heygen.status}` : ""}</div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setLesson(l)} data-testid={`edit-lesson-${l.id}`}><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteLesson(l)} data-testid={`delete-lesson-${l.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                  </div>
                ))}
                {m.lessons.length === 0 && <div className="py-3 text-sm text-muted-foreground">No lessons yet.</div>}
              </div>
            </div>
          ))}
        </div>

        {courseForm && <CourseDialog form={courseForm} setForm={setCourseForm} onSave={saveCourse} />}
        {lesson && <LessonEditor lesson={lesson} courseId={current.id} onClose={async (changed) => { setLesson(null); if (changed) { await loadCourse(current.id); await loadList(); } }} />}
      </div>
    );
  }

  return (
    <div data-testid="admin-courses-page">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-[22px] font-semibold">Courses</h2>
          <p className="text-sm text-muted-foreground">Create and maintain courses, modules, lessons, videos and narration.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={refreshContent} disabled={refreshing} data-testid="refresh-content-button">
            {refreshing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />} Reload built-in content
          </Button>
          <Button className="btn-brand" onClick={() => setCourseForm({ ...EMPTY })} data-testid="new-course-button"><Plus className="mr-1.5 h-4 w-4" /> New course</Button>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {courses.map((c) => (
          <button key={c.id} onClick={() => loadCourse(c.id)} className="card-surface p-5 text-left transition-colors hover:bg-accent/40" data-testid={`admin-course-card-${c.id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{c.category}</span>
              {c.is_published ? <Eye className="h-4 w-4 text-emerald-500" /> : <EyeOff className="h-4 w-4 text-amber-500" />}
            </div>
            <div className="mt-2 font-heading text-[17px] font-semibold leading-snug">{c.title}</div>
            <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
              <span>{c.lesson_count} lessons</span>
              <span className="inline-flex items-center gap-1"><Video className="h-3.5 w-3.5" /> {c.video_count}/{c.lesson_count} videos</span>
            </div>
            <div className="mt-3 flex justify-end">
              <span className="text-xs text-destructive hover:underline" onClick={(e) => { e.stopPropagation(); deleteCourse(c); }} data-testid={`delete-course-${c.id}`}>Delete</span>
            </div>
          </button>
        ))}
      </div>
      {courseForm && <CourseDialog form={courseForm} setForm={setCourseForm} onSave={saveCourse} />}
    </div>
  );
}

function CourseDialog({ form, setForm, onSave }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  const toggleRole = (r) => set("visibility_roles", form.visibility_roles.includes(r) ? form.visibility_roles.filter((x) => x !== r) : [...form.visibility_roles, r]);
  return (
    <Dialog open onOpenChange={() => setForm(null)}>
      <DialogContent className="max-w-lg" data-testid="course-dialog">
        <DialogHeader><DialogTitle>{form.id ? "Course settings" : "New course"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Course title" data-testid="course-title-input" />
          <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Short description" rows={3} data-testid="course-description-input" />
          <div className="grid grid-cols-2 gap-3">
            <Input value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="Category" data-testid="course-category-input" />
            <Input type="number" min={50} max={100} value={form.pass_mark} onChange={(e) => set("pass_mark", Number(e.target.value))} placeholder="Pass mark %" data-testid="course-passmark-input" />
          </div>
          <div>
            <div className="mb-1.5 text-xs font-medium text-muted-foreground">Visible to</div>
            <div className="flex flex-wrap gap-2">
              {ROLES.map((r) => (
                <button key={r} type="button" onClick={() => toggleRole(r)} data-testid={`course-role-${r}`}
                  className={`rounded-full border px-3 py-1 text-xs ${form.visibility_roles.includes(r) ? "border-[#1779AC] bg-[#1779AC] text-white" : "border-border text-muted-foreground"}`}>{ROLE_LABEL[r]}</button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.sequential_unlock} onChange={(e) => set("sequential_unlock", e.target.checked)} data-testid="course-sequential-checkbox" /> Lessons unlock in order</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_published} onChange={(e) => set("is_published", e.target.checked)} data-testid="course-published-checkbox" /> Published (visible to members)</label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
            <Button className="btn-brand" onClick={onSave} disabled={!form.title.trim()} data-testid="save-course-button">Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
